import React, { useCallback } from "react"
import type { MutableRefObject, Ref } from "react"
import { cn } from "../utils"

import { Button } from "../button"
import { LinkButton } from "../link-button"
import type { ButtonSize } from "../button-base"
import type { ActionElement, ActionItem } from "./types"

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === "function") ref(node)
  else if (ref) (ref as MutableRefObject<T | null>).current = node
}

export type ActionButtonProps = {
  action: ActionItem
  size?: ButtonSize
  className?: string
  /**
   * Receives the rendered node in addition to any ref on the `Action` itself —
   * `ConfirmModal` uses it to claim its least-destructive focus target without
   * taking the ref away from the consumer.
   */
  focusRef?: Ref<ActionElement>
}

/**
 * Renders a registered action. Every prop is passed explicitly rather than
 * spread from `action`, so a stray key on the registered props can never
 * overwrite `ref`, `className`, or the resolved variant.
 */
export const ActionButton = ({
  action,
  size,
  className,
  focusRef
}: ActionButtonProps) => {
  const {
    variant,
    children,
    className: actionClassName,
    onClick,
    disabled,
    loading,
    form,
    href,
    style,
    type,
    target,
    rel,
    telemetry,
    ref: actionRef,
    "data-testid": dataTestId
  } = action

  const setRef = useCallback(
    (node: ActionElement | null) => {
      assignRef(actionRef, node)
      assignRef(focusRef, node)
    },
    [actionRef, focusRef]
  )

  // `size` is meaningless for `unstyled` and the variant props are typed to
  // forbid it there, so the two cases have to be built separately. An omitted
  // `variant` falls through to `Button`'s own `primary` default.
  const variantProps =
    variant === "unstyled"
      ? ({ variant: "unstyled" } as const)
      : ({ variant, size } as const)

  const classes = cn(className, actionClassName)

  if (href) {
    return (
      <LinkButton
        {...variantProps}
        ref={setRef}
        href={href}
        className={classes}
        onClick={onClick}
        style={style}
        target={target}
        rel={rel}
        telemetry={telemetry}
        aria-disabled={disabled || undefined}
        data-testid={dataTestId}
      >
        {children}
      </LinkButton>
    )
  }

  return (
    <Button
      {...variantProps}
      ref={setRef}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      form={form}
      style={style}
      type={type}
      telemetry={telemetry}
      data-testid={dataTestId}
    >
      {children}
    </Button>
  )
}
